import { clsx } from 'clsx';
import { Select } from 'antd';
import { Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNotify, useRedirect } from 'react-admin';

import { InitialPage } from '@components';
import { Credit, Order, OrderCompositionProduct, ProductOriginType } from '@types';
import {
  CalculateInvoiceResult,
  creditHttp,
  ordersHttp,
  SaveOrderParams,
} from '@network';
import { axiosErrorToString } from '@utils';
import {
  CreditInvoiceProducts,
} from '@pages/credit/CreditEdit/CreditInvoices/CreditInvoiceProducts';

import cls from './order-create.module.css';

const MAX_POSITIONS = 60;

export const OrderCreate = ({ order }: {order?: Order}) => {
  const redirect = useRedirect();
  const notify = useNotify();

  const [composition, setComposition] = useState<OrderCompositionProduct[]>([]);
  const [calculate, setCalculate] = useState<CalculateInvoiceResult>();
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [isrPercent, setIsrPercent] = useState<number | null>(null);
  const [iepsPercent, setIepsPercent] = useState<number | null>(null);

  const [usersList, setUsersList] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [credits, setCredits] = useState<Credit[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedCreditId, setSelectedCreditId] = useState<number | null>(null);
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const onUserSelect = (userId: number) => {
    setSelectedUserId(userId);
    const userCredits = usersList
      .find(user => user.userId === userId)?.credits
      .filter((c: any) => !c.createdByDealerId) || [];

    setSelectedCreditId(null);
    setCredits(userCredits
      .map((credit: any) => ({
        value: credit.creditId,
        label: credit.code,
      })));
  };

  const onCreditSelect = (creditId: number) => {
    setSelectedCreditId(creditId);
  };

  const submit = async () => {
    setShowErrors(false);

    const selectedComposition = composition.filter(c => c.productId);
    if (!selectedUserId) {
      setShowErrors(true);

      return;
    }

    if (selectedComposition.length === 0) {
      notify('Add at least one product', { type: 'error' });

      return;
    }

    const params: SaveOrderParams = {
      id: order?.id,
      userId: selectedUserId,
      creditId: selectedCreditId,
      composition: selectedComposition,
      discountAmount,
      isrPercent,
      iepsPercent,
    };

    try {
      const savedOrder = await ordersHttp.saveOrder(params);
      redirect(`/order/${savedOrder.id}`);
    } catch (e) {
      notify(axiosErrorToString(e), { type: 'error' });
      setShowErrors(true);
    }
  };

  useEffect(() => {
    if (!order) return;
    setDiscountAmount(order?.discountAmount ?? 0);
    setIsrPercent(order?.isrPercent ?? null);
    setIepsPercent(order?.iepsPercent ?? null);
  }, [order]);

  useEffect(() => {
    (async () => {
      try {
        const loadedUsers = await creditHttp.getAllUsers();
        const sortedUsers = loadedUsers.sort((a, b) => a.fullName?.localeCompare(b.fullName));
        setUsersList(sortedUsers);
        setUsers(sortedUsers.map(u => ({ value: u.userId, label: u.fullName })));
      } catch (e) {
        notify(axiosErrorToString(e), { type: 'error' });
      }
    })();
  }, []);

  const calculateRequest = async () => {
    if (composition.length === 0) return;

    try {
      const nextCalculate = await creditHttp.calculateInvoice({
        composition,
        discountAmount,
        isrPercent,
        iepsPercent,
      });
      setCalculate(nextCalculate);
    } catch (err: unknown) {
      console.error(err);
    }
  };

  useEffect(() => void calculateRequest(), [
    composition, discountAmount, isrPercent, iepsPercent,
  ]);

  const showUserError = !selectedUserId && showErrors;

  return (
    <InitialPage title="Create order" backTo="/order" backText="Back to all orders">
      <div className={cls._}>
        <div className={cls.head}>
          <div className={cls.avatar}><AvatarIcon /></div>
          <div className={cls.info}>
            <div className={cls.infoRow}>
              <div className={cls.label}>User</div>
              <div className={cls.value}>
                <Select
                  onChange={(userId) => onUserSelect(userId)}
                  value={selectedUserId}
                  className={clsx(cls.select, showUserError && cls.error)}
                  showSearch
                  placeholder="Select user"
                  options={users}
                  filterSort={(a, b) => `${a.label}`.localeCompare(`${b.label}`)}
                  filterOption={(input, option) =>
                    `${option?.label}`.toLowerCase().includes(input.toLowerCase())}
                />
              </div>
            </div>
            <div className={cls.infoRow}>
              <div className={cls.label}>Credit</div>
              <div className={cls.value}>
                <Select
                  onChange={(creditId) => onCreditSelect(creditId)}
                  value={selectedCreditId}
                  className={clsx(cls.select, cls.selectBold)}
                  showSearch
                  placeholder="Select credit"
                  options={credits}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={cls.products}>
          <div className={cls.title}>Products to quote</div>
          <CreditInvoiceProducts
            composition={composition}
            setComposition={setComposition}
            calculate={calculate}
            discountAmount={discountAmount}
            setDiscountAmount={setDiscountAmount}
            isrPercent={isrPercent}
            setIsrPercent={setIsrPercent}
            iepsPercent={iepsPercent}
            setIepsPercent={setIepsPercent}
            max={MAX_POSITIONS}
            origin={ProductOriginType.ORDER}
          />
        </div>

        <div className={cls.actions}>
          <Button className={cls.save} variant="contained" onClick={submit}>
            Create order
          </Button>
        </div>
      </div>
    </InitialPage>
  );
};

const AvatarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg"
    width="88"
    height="88"
    viewBox="0 0 88 88"
    fill="none">
    <path d="M44 0C19.7057 0 0 19.7057 0 44C0 68.2943 19.7057 88 44 88C68.2943 88 88 68.2943 88 44C88 19.7057 68.2943 0 44 0ZM44 22C53.9 22 61.9457 30.0457 61.9457 39.9457C61.9457 49.8457 53.9 57.8914 44 57.8914C34.1 57.8914 26.0543 49.8457 26.0543 39.9457C26.0543 30.0457 34.1 22 44 22ZM44 82.3429C35.0743 82.3429 26.84 79.2629 20.3029 74.1086C22.6286 70.7771 26.4629 68.7657 30.5486 68.7657H57.4514C61.5371 68.7657 65.3714 70.7771 67.6971 74.1086C61.16 79.2629 52.9257 82.3429 44 82.3429Z"
      fill="#042E6B" />
  </svg>
);
